// ===== Packages =====

import {
    FirebaseError,
}                               from 'firebase/app';
import {
    AuthProvider,
    AuthCredential,
    User,
    UserCredential,
    signInWithPopup,
    signInWithRedirect,
    signInWithCredential,
    linkWithPopup,
    GoogleAuthProvider,
    FacebookAuthProvider,
    TwitterAuthProvider,
    GithubAuthProvider,
    signInWithEmailAndPassword,
    setPersistence,
    browserSessionPersistence,
}                               from 'firebase/auth';

// ===== Firebase =====

import fire                     from '../fire';

// ===== Services =====

import {
    saveStateForRedirect,
    logAuthDebug,
}                               from '.';

// ===== Interfaces =====

import {
    ICartItem,
    IPartialUserItem,
}                               from '../interfaces';

// ===== Enums =====

import {
    AUTHENTICATION_ERROR_CODE,
    AUTHENTICATION_TYPE,
}                               from '../enums';

// ===== Initialize Firebase =====

const { auth } = fire();

interface SignInProps {
    type: AUTHENTICATION_TYPE,
    hasSound: boolean,
    signingInOut: boolean,
    oldUserData: {
        user: IPartialUserItem | undefined,
        email: string | undefined,
        userSessions: string[] | undefined,
        userUpdateTimestamps: number[] | undefined,
        oldUserCart?: ICartItem[],
    },
    credentials?: {
        email: string,
        password: string,
    },
    onComplete: (user: User | null, accessToken?: string | undefined) => void,
    onError: (error: FirebaseError) => void,
}

type ProviderSignInType =
    AUTHENTICATION_TYPE.google
    | AUTHENTICATION_TYPE.facebook
    | AUTHENTICATION_TYPE.xTwitter
    | AUTHENTICATION_TYPE.github;

const isProviderSignInType = (type: AUTHENTICATION_TYPE): type is ProviderSignInType => (
    type === AUTHENTICATION_TYPE.google
    || type === AUTHENTICATION_TYPE.facebook
    || type === AUTHENTICATION_TYPE.xTwitter
    || type === AUTHENTICATION_TYPE.github
);

const createProviderForType = (type: ProviderSignInType): AuthProvider => {
    switch (type) {
    case AUTHENTICATION_TYPE.google:
        return new GoogleAuthProvider();
    case AUTHENTICATION_TYPE.facebook:
        return new FacebookAuthProvider();
    case AUTHENTICATION_TYPE.xTwitter:
        return new TwitterAuthProvider();
    case AUTHENTICATION_TYPE.github:
        return new GithubAuthProvider();
    default:
        return new GoogleAuthProvider();
    }
};

const getProviderAccessToken = (
    type: ProviderSignInType,
    result: UserCredential,
): string | undefined => {
    switch (type) {
    case AUTHENTICATION_TYPE.google:
        return GoogleAuthProvider.credentialFromResult(result)?.accessToken || undefined;
    case AUTHENTICATION_TYPE.facebook:
        return FacebookAuthProvider.credentialFromResult(result)?.accessToken || undefined;
    case AUTHENTICATION_TYPE.xTwitter:
        return TwitterAuthProvider.credentialFromResult(result)?.accessToken || undefined;
    case AUTHENTICATION_TYPE.github:
        return GithubAuthProvider.credentialFromResult(result)?.accessToken || undefined;
    default:
        return undefined;
    }
};

const isLinkFallbackError = (errorCode?: string): boolean => (
    errorCode === AUTHENTICATION_ERROR_CODE.accountExistsWithDifferentCredential
    || errorCode === AUTHENTICATION_ERROR_CODE.credentialAlreadyInUse
    || errorCode === AUTHENTICATION_ERROR_CODE.popupBlocked
    || errorCode === AUTHENTICATION_ERROR_CODE.cancelledPopupRequest
    || errorCode === AUTHENTICATION_ERROR_CODE.operationNotSupportedInEnvironment
);

const shouldFallbackToRedirectFromPopupError = (errorCode?: string): boolean => (
    errorCode === AUTHENTICATION_ERROR_CODE.popupBlocked
    || errorCode === AUTHENTICATION_ERROR_CODE.cancelledPopupRequest
    || errorCode === AUTHENTICATION_ERROR_CODE.operationNotSupportedInEnvironment
);

const getCredentialFromProviderError = (
    type: ProviderSignInType,
    error: FirebaseError,
): AuthCredential | null => {
    switch (type) {
    case AUTHENTICATION_TYPE.google:
        return GoogleAuthProvider.credentialFromError(error);
    case AUTHENTICATION_TYPE.facebook:
        return FacebookAuthProvider.credentialFromError(error);
    case AUTHENTICATION_TYPE.xTwitter:
        return TwitterAuthProvider.credentialFromError(error);
    case AUTHENTICATION_TYPE.github:
        return GithubAuthProvider.credentialFromError(error);
    default:
        return null;
    }
};

const saveRedirectAuthState = ({
    hasSound,
    signingInOut,
    oldUserData,
}: {
    hasSound: boolean,
    signingInOut: boolean,
    oldUserData: SignInProps['oldUserData'],
}): void => {
    const oldUserDataSummary = {
        hasUser: !!oldUserData.user,
        oldUserId: oldUserData.user?.id,
        hasEmail: !!oldUserData.email,
        sessionsCount: oldUserData.userSessions?.length || 0,
        updatedCount: oldUserData.userUpdateTimestamps?.length || 0,
        cartCount: oldUserData.oldUserCart?.length || 0,
    };
    logAuthDebug('provider-redirect-state-save-started', {
        hasSound,
        signingInOut,
        oldUserData: oldUserDataSummary,
    });
    saveStateForRedirect({
        // include only SMALL, necessary bits of UI state
        scrollY: window.scrollY,
        hasSound,
        signingInOut,
        oldUserData,
    });
    try {
        sessionStorage.setItem('vs_redirecting', '1');
        logAuthDebug('provider-redirect-marker-saved', {
            storage: 'sessionStorage',
        });
    } catch (error) {
        try {
            localStorage.setItem('vs_redirecting', '1');
            logAuthDebug('provider-redirect-marker-saved', {
                storage: 'localStorage',
                sessionStorageError: (error as Error)?.message || String(error),
            });
        } catch (fallbackError) {
            logAuthDebug('provider-redirect-marker-save-failed', {
                sessionStorageError: (error as Error)?.message || String(error),
                localStorageError: (fallbackError as Error)?.message || String(fallbackError),
            });
        }
    }
};

const canLinkAnonymousUser = (oldUserData: SignInProps['oldUserData']): boolean => (
    !!auth.currentUser
    && auth.currentUser.isAnonymous
    && !!oldUserData.user
    && oldUserData.user.id === auth.currentUser.uid
);

const signInWithProviderPopup = async ({
    type,
    provider,
    oldUserData,
}: {
    type: ProviderSignInType,
    provider: AuthProvider,
    oldUserData: SignInProps['oldUserData'],
}): Promise<{
    user: User,
    accessToken?: string,
}> => {
    const shouldLinkAnonymous = canLinkAnonymousUser(oldUserData);
    let result: UserCredential;

    if (shouldLinkAnonymous && auth.currentUser) {
        try {
            result = await linkWithPopup(auth.currentUser, provider);
        } catch (error) {
            const authError = error as FirebaseError;
            if (!isLinkFallbackError(authError.code)) {
                throw authError;
            }
            const providerCredential = getCredentialFromProviderError(type, authError);
            if (providerCredential) {
                result = await signInWithCredential(auth, providerCredential);
            } else {
                result = await signInWithPopup(auth, provider);
            }
        }
    } else {
        result = await signInWithPopup(auth, provider);
    }

    return {
        user: result.user,
        accessToken: getProviderAccessToken(type, result),
    };
};

const signInWithProviderRedirect = async ({
    provider,
    hasSound,
    signingInOut,
    oldUserData,
}: {
    provider: AuthProvider,
    hasSound: boolean,
    signingInOut: boolean,
    oldUserData: SignInProps['oldUserData'],
}): Promise<void> => {
    const authDomain = (
        (auth as unknown as { config?: { authDomain?: string } }).config?.authDomain
        || (auth as unknown as { app?: { options?: { authDomain?: string } } }).app?.options?.authDomain
        || null
    );
    const expectedRedirectUri = authDomain
        ? `https://${authDomain}/__/auth/handler`
        : null;
    saveRedirectAuthState({
        hasSound,
        signingInOut,
        oldUserData,
    });
    logAuthDebug('provider-redirect-launch-started', {
        providerId: provider.providerId,
        authDomain,
        expectedRedirectUri,
    });
    // Keep explicit session persistence for parity with historical behavior, but
    // do not block redirect launch on this async step (can break gesture chain).
    setPersistence(auth, browserSessionPersistence)
        .then(() => {
            logAuthDebug('provider-redirect-persistence-set', {
                persistence: 'browserSessionPersistence',
            });
        })
        .catch((error) => {
            logAuthDebug('provider-redirect-persistence-failed', {
                error: (error as Error)?.message || String(error),
            });
        });

    // Redirect mode intentionally signs in directly instead of link-first.
    // linkWithRedirect is less reliable on iOS/Safari gesture/popup heuristics
    // and can surface popup-blocked style errors. We rely on shared migration to
    // carry anonymous data to the authenticated user when UID changes.
    logAuthDebug('provider-redirect-dispatched', {
        providerId: provider.providerId,
        authDomain,
        expectedRedirectUri,
    });
    await signInWithRedirect(auth, provider);
};

// Popup Blocked Reference:
// https://stackoverflow.com/questions/39898356/firebase-signinwithpopup-gives-auth-popup-blocked-when-used-via-built-in-browser
const signIn = async ({
    type,
    hasSound,
    signingInOut,
    oldUserData,
    credentials,
    onComplete,
    onError,
}: SignInProps): Promise<void> => {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const platform = typeof navigator !== 'undefined' ? navigator.platform : '';
    const maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0;
    const userAgentDataMobile = typeof navigator !== 'undefined'
        ? ((navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile === true)
        : false;
    const coarsePointer = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
    // iPadOS desktop-mode user agents report "MacIntel" while still being iOS-class.
    const isIPadOSDesktop = platform === 'MacIntel' && maxTouchPoints > 1;
    const isIOS = /\b(iPhone|iPad|iPod)\b/i.test(userAgent) || isIPadOSDesktop;
    const isSafari = /\bSafari\b/i.test(userAgent) && !/\b(Chrome|CriOS|Edg|FxiOS)\b/i.test(userAgent);
    const preferRedirect = isIOS || isIPadOSDesktop || userAgentDataMobile || coarsePointer;
    logAuthDebug('provider-auth-mode-resolved', {
        type,
        preferRedirect,
        isIOS,
        isSafari,
        isIPadOSDesktop,
        userAgentDataMobile,
        coarsePointer,
        platform,
        maxTouchPoints,
    });

    if (isProviderSignInType(type)) {
        const provider = createProviderForType(type);

        if (preferRedirect) {
            try {
                await signInWithProviderRedirect({
                    provider,
                    hasSound,
                    signingInOut,
                    oldUserData,
                });
            } catch (error) {
                logAuthDebug('provider-redirect-launch-failed', {
                    providerId: provider.providerId,
                    code: (error as FirebaseError)?.code,
                    message: (error as Error)?.message || String(error),
                });
                onError(error as FirebaseError);
            }
            // Redirect auth completes on the next page load; restored state + auth listener
            // perform post-sign-in setup, so there is no immediate user to pass to onComplete.
            // Do not call onComplete here: redirect auth completes after navigation/reload.
            // App redirect restore + auth listeners handle the post-sign-in completion path.
            return;
        }

        try {
            const result = await signInWithProviderPopup({
                type,
                provider,
                oldUserData,
            });
            logAuthDebug('provider-popup-completed', {
                providerId: provider.providerId,
                userId: result.user?.uid,
            });
            onComplete(result.user, result.accessToken);
            return;
        } catch (error) {
            const authError = error as FirebaseError;
            logAuthDebug('provider-popup-failed', {
                providerId: provider.providerId,
                code: authError.code,
                message: authError.message,
            });
            if (shouldFallbackToRedirectFromPopupError(authError.code)) {
                try {
                    await signInWithProviderRedirect({
                        provider,
                        hasSound,
                        signingInOut,
                        oldUserData,
                    });
                    logAuthDebug('provider-popup-fallback-to-redirect-dispatched', {
                        providerId: provider.providerId,
                        originalCode: authError.code,
                    });
                    return;
                } catch (redirectError) {
                    logAuthDebug('provider-popup-fallback-to-redirect-failed', {
                        providerId: provider.providerId,
                        originalCode: authError.code,
                        fallbackCode: (redirectError as FirebaseError)?.code,
                        fallbackMessage: (redirectError as Error)?.message || String(redirectError),
                    });
                    onError(redirectError as FirebaseError);
                    return;
                }
            }
            onError(authError);
            return;
        }
    }

    if (credentials) {
        signInWithEmailAndPassword(auth, credentials.email, credentials.password)
            .then((userCredential) => {
                // Signed in
                const { user } = userCredential;
                onComplete(user);
            })
            .catch((error) => {
                onError(error);
            });
    }
};

export default signIn;
